import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useAtom } from "jotai";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { Textarea } from "rizzui";
import * as yup from 'yup';
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import RequiredInput from "../../../components/forms/InputHOC";
import { PopoverPicker } from "../../../components/forms/PopoverPicker";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { PickupGlobalMessageService } from "../../../services/pickup/pickupGlobalMessageService";
import { PickupGlobalMessage } from "../../../type/entities/pickup-global-message-type";
import { pickupMessagesTypes } from "../../../utils/SelectorMapping";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useUsers from "../../../hooks/api-calls/useUsers";

export interface PickupGlobalMessageFormProps {
    open: boolean;
    onClose: () => void;
    pickupGlobalMessageSelected: PickupGlobalMessage | null;
    formRef?: any;
}

const schema = yup.object({
    startDate: yup.date().required('La fecha inicial es obligatoria'),
    message: yup.string().required('El mensaje es obligatorio'),
    color: yup.string().required('El color es obligatorio'),
    concept: yup.string().required('El concepto es obligatorio'),
    type: yup.string().required('El tipo es obligatorio'),
    endDate: yup.date().nullable().test(
        'is-greater',
        'La fecha final debe ser posterior a la fecha inicial',
        function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return moment(value).isAfter(moment(startDate));
        }
    ),
});

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La empresa es obligatoria'),
}));

const PickupGlobalMessageForm: FC<PickupGlobalMessageFormProps> = ({
    open,
    onClose,
    pickupGlobalMessageSelected,
    formRef
}) => {

    // STATES

    const isEdit = pickupGlobalMessageSelected !== null;
    const service = new PickupGlobalMessageService();
    const [isSearched, setIsSearched] = useState(false);

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getUsersList, updateHookFilters } = useUsers();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: pickupGlobalMessageSelected?.id ?? '',
            startDate: pickupGlobalMessageSelected?.startDate?.date ?? '',
            endDate: pickupGlobalMessageSelected?.endDate?.date ?? '',
            message: pickupGlobalMessageSelected?.message ?? '',
            color: pickupGlobalMessageSelected?.color ?? '#607d8b',
            concept: pickupGlobalMessageSelected?.concept ?? 'general',
            type: pickupGlobalMessageSelected?.type ?? 'info',
            company: pickupGlobalMessageSelected?.company?.id ?? '',
            user: pickupGlobalMessageSelected?.creator?.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO MENSAJE GLOBAL DE RECOGIDA
     * @EN CREATE A NEW PICKUP GLOBAL MESSAGE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const formattedValues = {
                ...values,
                startDate: values.startDate ? moment(values.startDate).format('YYYY-MM-DD HH:mm:ss') : null,
                endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD HH:mm:ss') : null,
            };

            const response = await (await service.createPickupGlobalMessage(formattedValues)).getResponseData();
            if (response.success) {
                formik.resetForm();
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN MENSAJE GLOBAL DE RECOGIDA
     * @EN EDIT A PICKUP GLOBAL MESSAGE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const formattedValues = {
                ...values,
                startDate: values.startDate ? moment(values.startDate).format('YYYY-MM-DD HH:mm:ss') : null,
                endDate: values.endDate ? moment(values.endDate).format('YYYY-MM-DD HH:mm:ss') : null,
            };

            const response = await (await service.editPickupGlobalMessage(formattedValues)).getResponseData();
            if (response.success) {
                formik.resetForm();
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    useEffect(() => {
        if (formik.values.company) {
            updateHookFilters({ filter_filters: { companyId: formik.values.company } });
        }
    }, [formik.values.company]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <div className="md:col-span-12">
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                                Fecha inicial <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                monthsShown={1}
                                placeholderText="Selecciona una fecha inicial"
                                inputProps={{
                                    inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.startDate ? 'border-red-500' : ''}`,
                                }}
                                className="col-span-12"
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                //@ts-ignore
                                maxDate={formik.values.endDate ? moment(formik.values.endDate).toDate() : null}
                                //@ts-ignore
                                selected={formik.values?.startDate ? moment(formik.values.startDate).toDate() : null}
                                onChange={(date: any) => {
                                    formik.setFieldValue('startDate', date);
                                }}
                                locale={es}
                            />
                            {formik.errors.startDate && (
                                <p className="text-red-500 text-sm">{formik.errors.startDate?.toString()}</p>
                            )}
                        </div>

                        <div className="md:col-span-12">
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                                Fecha final (opcional)
                            </label>
                            <DatePicker
                                monthsShown={1}
                                placeholderText="Selecciona una fecha final"
                                inputProps={{
                                    inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.endDate ? 'border-red-500' : ''}`,
                                }}
                                className="col-span-12"
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="dd/MM/yyyy HH:mm"
                                //@ts-ignore
                                minDate={formik.values?.startDate ? moment(formik.values.startDate).toDate() : null}
                                //@ts-ignore
                                selected={formik.values?.endDate ? moment(formik.values.endDate).toDate() : null}
                                onChange={(date: any) => {
                                    formik.setFieldValue('endDate', date);
                                }}
                                locale={es}
                                isClearable
                            />
                            {formik.errors.endDate && (
                                <p className="text-red-500 text-sm">{formik.errors.endDate?.toString()}</p>
                            )}
                        </div>

                        <div className="md:col-span-12">
                            <RequiredInput
                                id="concept"
                                label="Concepto"
                                placeholder="Ingresa el concepto del mensaje"
                                formik={formik}
                                required={true}
                            />
                        </div>

                        <div className="md:col-span-6">
                            <CustomSelect
                                id="type"
                                label="Tipo"
                                placeholder="Selecciona el tipo de mensaje"
                                options={pickupMessagesTypes}
                                value={pickupMessagesTypes.find(option => option.value === formik.values.type)}
                                onChange={(selectedOption: any) => {
                                    formik.setFieldValue('type', selectedOption?.value || '');
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.type && formik.errors.type ? formik.errors.type : null}
                                required={true}
                                isClearable={false}
                            />
                        </div>

                        {/* Input to change hexadecimal color */}
                        <div className="md:col-span-6">
                            <RequiredInput
                                id="color"
                                label="Color"
                                placeholder="Ingresa el color en hexadecimal (ej. #FF0000)"
                                formik={formik}
                                required={true}
                            />
                        </div>

                        {userCan('list', 'company') && (
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                                }
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('company', e?.value) }
                                }
                                error={formik.errors.company}
                            />
                        )}

                        {userCan('list', 'user') && (
                             <CustomSelect
                                required
                                isSearchable
                                id={'user'}
                                label="Usuario"
                                containerClassName='md:col-span-12'
                                value={
                                    { value: formik.values.user, label: getUsersList()?.find((g: any) => g.value === formik.values.user)?.label }
                                }
                                options={getUsersList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('user', e?.value) }
                                }
                                error={formik.errors.user}
                            />
                        )}

                        <div className="md:col-span-12">
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                Mensaje <span className="text-red-500">*</span>
                            </label>
                            <Textarea
                                id="message"
                                placeholder="Escribe el mensaje global para las recogidas"
                                rows={4}
                                value={formik.values.message}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={`w-full ${formik.errors.message ? 'border-red-500' : ''}`}
                            />
                            {formik.errors.message && (
                                <p className="text-red-500 text-sm">{formik.errors.message?.toString()}</p>
                            )}
                        </div>

                        <div className="md:col-span-12">
                            <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">
                                Color <span className="text-red-500">*</span>
                            </label>
                            <div className="flex items-center gap-3">
                                <PopoverPicker
                                    color={formik.values.color}
                                    onChange={(color: string) => formik.setFieldValue('color', color)}
                                />
                                <span className="text-sm text-gray-600">
                                    Color seleccionado: {formik.values.color}
                                </span>
                            </div>
                            {formik.errors.color && (
                                <p className="text-red-500 text-sm">{formik.errors.color?.toString()}</p>
                            )}
                        </div>

                    </div>
                </div>
            </form>
        </div>
    )
}

export default PickupGlobalMessageForm;